/* eslint-disable @typescript-eslint/no-floating-promises */
import { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { defer, useLoaderData, useSearchParams } from '@remix-run/react'
import { compact, isEmpty } from 'lodash'
import { useState } from 'react'
import TrackableLongLogo from 'app/components/Icon/TrackableLongLogo'
import { AppointmentConfig } from 'app/components/scheduling/AppointmentConfig'
import { AppointmentConfirmation } from 'app/components/scheduling/AppointmentConfirmation'
import { AppointmentForm } from 'app/components/scheduling/AppointmentForm'
import { AppointmentTypeSelector } from 'app/components/scheduling/AppointmentType'
import { OverrideConfirmation } from 'app/components/scheduling/OverrideConfirmation'
import { OverrideForm } from 'app/components/scheduling/OverrideForm'
import { PatientStatus } from 'app/components/scheduling/PatientStatus'
import SchedulingWizard from 'app/components/scheduling/SchedulingWizard'
import { useDeferredData } from 'app/hooks/useDeferredData'
import { AppointmentTypeType } from 'app/models/AppointmentType'
import { AvailabilityType } from 'app/models/Availability'
import { ProviderType } from 'app/models/Provider'
import { DaySlotType, PatientStatusType, loadSchedulingDataProcessed } from 'lib/scheduling'

export const meta: MetaFunction = ({ data }: { data: any }) => [
  { title: `${data?.location?.name ? `${data?.location?.name} -` : ``} Schedule an Appointment` },
  { description: `Schedule an appointment at ${data?.location?.name}` },
  { name: `robots`, content: `noindex` },
]

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { searchParams } = new URL(request.url)
  const { id } = params

  if (!id) {
    throw new Response(null, { status: 404 })
  }

  const day = searchParams.get(`day`) || undefined
  // Use new server-side processed data
  const data = await loadSchedulingDataProcessed(id, day)
  return defer(data)
}

export default function SchedulingPage() {
  const data = useLoaderData<typeof loader>()
  const logoUrl: string | undefined = useDeferredData(data.logoUrl as Promise<string>)
  // Data is now processed server-side, no need for useDeferredData on these
  const providers = data.providers
  const availabilities = data.availabilities
  const slots = data.slots
  const addressLine1 = data.location.address
  const addressLine2 = compact([data.location.city, data.location.state, data.location.zip]).join(`, `)
  const [imgLoadFailed, setImgLoadFailed] = useState<boolean | undefined>()
  const [searchParams] = useSearchParams()
  // eslint-disable-next-line no-console
  console.log(`timing`, data.timing)

  const renderCorrectFlow = (
    regularFlow: React.ReactNode[],
    overrideFlow: React.ReactNode[],
    patientType?: PatientStatusType,
    slots?: DaySlotType[],
  ) => {
    // early return of regular flow if it is an existing patient, override only if patient type is new patient
    if (patientType === `Existing Patient`) return regularFlow

    // Data is now processed server-side, so no need to check for loading state
    // if override is enabled and no appointments in timeframe, show override flow
    if (
      data.schedulingConfiguration &&
      data.schedulingConfiguration?.leadOverrideEnabled &&
      data.schedulingConfiguration.leadOverrideConfig &&
      // if day IS present in the search params, it is the first calendar view,
      !searchParams.has(`day`) &&
      !slots?.some((slot, i) =>
        i <=
        data.schedulingConfiguration!.leadOverrideConfig!.amount *
          (data.schedulingConfiguration!.leadOverrideConfig!.unit === `weeks` ? 7 : 1) -
          1
          ? slot.timeSlots.some((timeSlot) => timeSlot.availabilities.length)
          : false,
      )
    )
      return overrideFlow

    return regularFlow
  }
  return (
    <div className="flex h-dvh flex-col bg-white md:bg-trn-100">
      <style
        dangerouslySetInnerHTML={{
          __html: `.react-calendar__month-view__days__day--neighboringMonth { display: block; visibility: hidden }`,
        }}
      ></style>
      <div className="flex h-full place-items-center md:grid">
        <div
          className="flex h-full w-full grow flex-col items-center justify-center rounded-xl bg-white shadow-elevation-4 md:h-fit md:min-h-[780px] md:w-[720px]"
          style={{ borderColor: `rgba(0, 0, 0, 0.06)` }}
        >
          <div
            className="flex
          w-full justify-between border-b px-6 py-4"
            style={{ borderColor: `#E5E7EB` }}
          >
            <div className="flex h-20 w-[300px] items-start bg-transparent">
              {logoUrl && !imgLoadFailed ? (
                <img
                  src={logoUrl}
                  height={80}
                  width={300}
                  className="h-20 w-auto object-contain"
                  alt={data.location.name}
                  onError={(e) => setImgLoadFailed(true)}
                />
              ) : (
                imgLoadFailed === true && <span className="text-ui-lg">{data.location.name}</span>
              )}
            </div>
            <div className="flex shrink-0 flex-col text-right text-xs font-medium text-neutral-700">
              {addressLine1 && <span>{addressLine1}</span>}
              {addressLine2 && <span>{addressLine2}</span>}
            </div>
          </div>
          <SchedulingWizard
            appointmentTypes={data.appointmentTypes as AppointmentTypeType[]}
            allProviders={providers as ProviderType[]}
            allAvailabilities={availabilities as AvailabilityType[]}
            // Pass pre-processed slots
            preProcessedSlots={slots as DaySlotType[]}
            location={data.location}
            lookaheadDays={data.lookaheadDays}
            startDate={data.day}
            schedulingConfiguration={data.schedulingConfiguration!}
          >
            {(patientType, slots) => (
              <>
                <SchedulingWizard.Steps>
                  <PatientStatus key="step-0" />
                  <AppointmentTypeSelector key="step-1" />
                  {renderCorrectFlow(
                    [
                      <AppointmentConfig
                        key="step-2"
                        location={data.location}
                        schedulingConfiguration={data.schedulingConfiguration!}
                        startDate={data.day}
                      />,
                      <AppointmentForm
                        key="step-3"
                        location={data.location}
                        schedulingConfiguration={data.schedulingConfiguration!}
                        startDate={data.day}
                      />,
                      <AppointmentConfirmation key="step-4" startDate={data.day} />,
                    ],
                    [
                      <OverrideForm
                        key="step-2"
                        location={data.location}
                        schedulingConfiguration={data.schedulingConfiguration!}
                      />,
                      <OverrideConfirmation key="step-3" />,
                    ],
                    patientType,
                    slots,
                  )}
                </SchedulingWizard.Steps>
                <SchedulingWizard.Buttons schedulingConfiguration={data.schedulingConfiguration!} />
              </>
            )}
          </SchedulingWizard>
        </div>
      </div>
      {!isEmpty(data.location) && (
        <div className="hidden shrink items-center justify-center gap-x-1 pb-4 md:flex">
          <span className="mr-1 text-body-xs text-neutral-400">Powered by</span>
          <TrackableLongLogo className="h-5 w-20 shrink-0 rounded-md bg-trn-100 text-trn-700" />
        </div>
      )}
    </div>
  )
}
